import React, { useState } from "react";
import {
  Grid,
  Button,
  TextField,
  Typography,
  Paper,
  Box,
  CircularProgress,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import { useMsal } from "@azure/msal-react";
import { loginRequest, signupRequest } from "constants/msalConfig";
import { callRedirectAPI } from "utilities/utils";
import axios from "axios";
import { config } from "utilities/config";

/**
 * Interprets GetIdentityRecordAsync response when status is 2xx.
 * Adjust if the API contract documents a specific shape.
 */
function identityRecordFound(data) {
  if (data == null) return false;
  if (typeof data === "boolean") return data;
  if (typeof data === "string") {
    const t = data.trim();
    if (!t || t.toLowerCase() === "null") return false;
    return true;
  }
  if (Array.isArray(data)) return data.length > 0;
  if (typeof data === "object") {
    const msg = (data.message || data.Message || "")
      .toString()
      .toLowerCase();
    if (msg.includes("not found") || msg.includes("no record")) return false;

    if (
      data.isFound === false ||
      data.IsFound === false ||
      data.success === false ||
      data.Success === false ||
      data.recordFound === false ||
      data.RecordFound === false ||
      data.exists === false ||
      data.Exists === false
    ) {
      return false;
    }
    if (
      data.isFound === true ||
      data.IsFound === true ||
      data.success === true ||
      data.Success === true ||
      data.recordFound === true ||
      data.RecordFound === true ||
      data.exists === true ||
      data.Exists === true
    ) {
      return true;
    }
    if (data.record != null && typeof data.record === "object") {
      return Object.keys(data.record).length > 0;
    }
    if (data.data != null) {
      return identityRecordFound(data.data);
    }
    const idKeys = [
      "contactid",
      "ContactId",
      "id",
      "Id",
      "identityid",
      "identityId",
      "IdentityId",
    ];
    if (idKeys.some((k) => data[k] != null && String(data[k]).trim() !== "")) {
      return true;
    }
    const keys = Object.keys(data);
    if (keys.length === 0) return false;
    const metaOnly = ["message", "Message", "statusCode", "StatusCode"];
    return keys.some((k) => !metaOnly.includes(k));
  }
  return false;
}

const useStyles = makeStyles((theme) => ({
  container: {
    padding: theme.spacing(4),
    minHeight: "80vh",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  paper: {
    padding: theme.spacing(4),
    maxWidth: 600,
    width: "100%",
  },
  title: {
    marginBottom: theme.spacing(2),
    textAlign: "center",
  },
  subtitle: {
    marginBottom: theme.spacing(3),
    textAlign: "center",
    color: theme.palette.text.secondary,
  },
  form: {
    marginTop: theme.spacing(2),
  },
  field: {
    marginBottom: theme.spacing(2),
  },
  verifyButton: {
    marginTop: theme.spacing(2),
    padding: theme.spacing(1.5),
  },
  verificationMessage: {
    marginTop: theme.spacing(3),
    padding: theme.spacing(3),
    backgroundColor: theme.palette.info.light,
    color: theme.palette.info.dark,
    borderRadius: theme.shape.borderRadius,
    textAlign: "center",
  },
  actionButton: {
    marginTop: theme.spacing(2),
    padding: theme.spacing(1.5),
  },
}));

function Registration() {
  const classes = useStyles();
  const { instance } = useMsal();
  const [passportNumber, setPassportNumber] = useState("");
  const [error, setError] = useState("");
  const [verifying, setVerifying] = useState(false);
  const [verificationResult, setVerificationResult] = useState(null); // 'found', 'notFound', null

  const handleChange = (e) => {
    const value = e.target.value;
    setPassportNumber(value);
    // Clear error when user starts typing
    if (error) {
      setError("");
    }
    // Clear verification result when user changes the passport number
    if (verificationResult) {
      setVerificationResult(null);
    }
  };

  const handleVerifyPassport = async () => {
    if (!passportNumber.trim()) {
      setError("Please enter your passport number");
      return;
    }

    setVerifying(true);
    setError("");
    setVerificationResult(null);

    const url =
      process.env.REACT_APP_GET_IDENTITY_RECORD_URL ||
      config.publicAPI.getIdentityRecord;
    const payload = { IdentityNumber: passportNumber.trim() };

    try {
      const res = await axios.post(url, payload, {
        headers: { "Content-Type": "application/json" },
      });
      const found = identityRecordFound(res.data);
      setVerificationResult(found ? "found" : "notFound");
    } catch (err) {
      const status = err.response && err.response.status;
      const body = err.response && err.response.data;
      const apiMsg =
        (body &&
          (body.message || body.Message || body.error || body.Error)) ||
        "";
      // API returns 400 + "No Record found" when identity is not on file (not 404)
      if (
        status === 404 ||
        (status === 400 && /no record/i.test(String(apiMsg)))
      ) {
        setVerificationResult("notFound");
        return;
      }
      setError(
        apiMsg ||
          err.message ||
          "Unable to verify your passport number right now. Please try again later."
      );
    } finally {
      setVerifying(false);
    }
  };

  const handleRegisterClick = () => {
    // Redirect to Azure B2C registration page
    callRedirectAPI(instance, signupRequest);
  };

  const handleLoginClick = () => {
    // Redirect to Azure B2C login page
    localStorage.clear();
    localStorage.setItem("isRedirect", false);
    localStorage.setItem("tknPay", "tokenRequest");
    localStorage.setItem("isSocial", false);
    callRedirectAPI(instance, loginRequest);
  };

  return (
    <Grid container className={classes.container}>
      <Grid item xs={12} sm={10} md={8} lg={6}>
        <Paper className={classes.paper} elevation={3}>
          <Typography variant="h4" className={classes.title}>
            Login/Register
          </Typography>
          <Typography variant="body1" className={classes.subtitle}>
            Please enter your Identity/Passport Number so we can verify your membership
          </Typography>

          <Box className={classes.form}>
            <TextField
              fullWidth
              label="Identity/Passport Number"
              name="passportNumber"
              value={passportNumber}
              onChange={handleChange}
              error={!!error}
              helperText={error}
              variant="outlined"
              className={classes.field}
              disabled={verifying}
            />
            <Button
              variant="contained"
              color="primary"
              onClick={handleVerifyPassport}
              disabled={verifying || !passportNumber.trim()}
              className={classes.verifyButton}
              fullWidth
            >
              {verifying ? (
                <>
                  <CircularProgress size={20} style={{ marginRight: 8 }} />
                  Verifying...
                </>
              ) : (
                "Verify"
              )}
            </Button>

            {/* Verification Result Messages */}
            {verificationResult === "found" && (
              <Box className={classes.verificationMessage}>
                <Typography variant="body1" gutterBottom>
                  Thank you. You are registered on the membership portal. Please login by clicking on the login button below.
                </Typography>
                <Button
                  variant="contained"
                  color="primary"
                  onClick={handleLoginClick}
                  className={classes.actionButton}
                  fullWidth
                >
                  Login
                </Button>
              </Box>
            )}

            {verificationResult === "notFound" && (
              <Box className={classes.verificationMessage}>
                <Typography variant="body1" gutterBottom>
                  Your Identity/Passport Number could not be found. Please register by clicking on the register button below.
                </Typography>
                <Button
                  variant="contained"
                  color="primary"
                  onClick={handleRegisterClick}
                  className={classes.actionButton}
                  fullWidth
                >
                  Register
                </Button>
              </Box>
            )}
          </Box>
        </Paper>
      </Grid>
    </Grid>
  );
}

export default Registration;




